/**
 * EditFocusSurface (S5, Drawer-evaluation ruling + CONSULTS §3) — the Edit
 * focus state: a committed-edit-only SINGLE CENTERED SURFACE with the
 * settings stacked beside the live Shape, over a dimmed, truly inert page
 * (TRUE interaction lockout via a full-viewport scrim — NEVER blur).
 * Esc and a visible Cancel are always available; on mobile the surface is
 * a bottom sheet.
 *
 * The four legibility signals (ALL required, CONSULTS §3):
 * 1. Pending-state styling on dirty fields in the same event tick (<100ms)
 *    — CommittedNumberField sets data-pending on keystroke.
 * 2. Minimal preview-stale indicator (dot/tag, never a banner) while any
 *    field is dirty.
 * 3. Commit-timed Shape transition — the live Shape re-renders ONLY on
 *    commit (the committed recipe is the single source), and the wrapper
 *    carries the transition; data-commit-tick marks each commit.
 * 4. The commit moment is signified — the per-field hint "Enter to apply ·
 *    applies when you leave this field" is tied to the pending state.
 *
 * The live Shape uses the SAME clamped model (buildShapeModel →
 * clampRecipeToFloor) — floor ticks and all; no separate preview math.
 * Form machinery reuse: AutoTemplateFormData shape + recipeToFormData
 * serializer keep the existing save path untouched.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BlogTemplateSummary } from '@/services/blogTemplatesService';
import { GenerationRecipe } from './recipeTypes';
import { AutoTemplateFormData } from './AutoTemplateConfigForm';
import { recipeToFormData } from './recipePayload';
import { ShapeFloorPayload } from './shape/shapeFloor';
import { RecipeShape } from './shape/RecipeShape';
import { CommittedNumberField } from './CommittedField';
import { GALLERY_STRINGS } from './galleryStrings';

export interface EditFocusSurfaceProps {
  template: BlogTemplateSummary;
  /** The committed starting recipe (from defaultConfigToRecipe). */
  initialRecipe: GenerationRecipe;
  /** Parsed v1.2 floor for the live clamped Shape. */
  floor?: ShapeFloorPayload;
  /** Persist: receives the COMMITTED recipe as AutoTemplateFormData. */
  onSave: (config: AutoTemplateFormData) => void;
  /** Abandon: Esc, Cancel button. */
  onCancel: () => void;
  isSaving?: boolean;
}

/**
 * THE editable-surface manifest — the single source of which recipe fields
 * this surface edits. The component RENDERS by mapping over this list, and
 * serializerRoundTrip.test.ts parameterizes its round-trip identity guard
 * over the same list (F3 ruling): a field added here without serializer
 * support fails CI by construction, never by convention.
 */
export interface SurfaceFieldDef {
  key:
    | 'targetWordCount'
    | 'minSections'
    | 'maxSections'
    | 'maxFAQItems'
    | 'maxCalloutsPerArticle'
    | 'minImages'
    | 'internalLinkCount';
  label: string;
  min?: number;
  max?: number;
}

export const SURFACE_FIELDS: readonly SurfaceFieldDef[] = [
  { key: 'targetWordCount', label: 'Target words', min: 300, max: 5000 },
  { key: 'minSections', label: 'Min sections', min: 1, max: 15 },
  { key: 'maxSections', label: 'Max sections', min: 1, max: 15 },
  { key: 'maxFAQItems', label: 'FAQ items', min: 1, max: 15 },
  { key: 'maxCalloutsPerArticle', label: 'Callouts', min: 0, max: 10 },
  { key: 'minImages', label: 'Images (min)', min: 0, max: 10 },
  { key: 'internalLinkCount', label: 'Internal links', min: 0, max: 20 },
] as const;

export const EditFocusSurface: React.FC<EditFocusSurfaceProps> = ({
  template,
  initialRecipe,
  floor,
  onSave,
  onCancel,
  isSaving = false,
}) => {
  // The COMMITTED recipe — the Shape's only source. Field edits land here
  // exclusively on commit (blur/Enter), never per keystroke.
  const [committed, setCommitted] = useState<GenerationRecipe>(initialRecipe);
  const [pendingFields, setPendingFields] = useState<ReadonlySet<string>>(new Set());
  const [commitTick, setCommitTick] = useState(0);

  const anyPending = pendingFields.size > 0;

  const handlePendingChange = (fieldKey: string, pending: boolean) => {
    setPendingFields((prev) => {
      const next = new Set(prev);
      if (pending) next.add(fieldKey);
      else next.delete(fieldKey);
      return next;
    });
  };

  const commitField = <K extends keyof GenerationRecipe>(key: K, value: GenerationRecipe[K]) => {
    setCommitted((prev) => ({ ...prev, [key]: value }));
    setCommitTick((t) => t + 1); // signal 3: transition starts exactly at commit
  };

  // Esc always available (mode made obvious, exit unambiguous).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const handleSave = () => onSave(recipeToFormData(committed));

  const renderField = (def: SurfaceFieldDef) => (
    <CommittedNumberField
      key={def.key}
      fieldKey={def.key}
      label={def.label}
      value={committed[def.key] as number | undefined}
      min={def.min}
      max={def.max}
      onCommit={(v) => commitField(def.key, v as GenerationRecipe[typeof def.key])}
      onPendingChange={handlePendingChange}
    />
  );

  const shapeKey = useMemo(() => commitTick, [commitTick]);

  return (
    <div data-testid="edit-focus-lockout" className="fixed inset-0 z-50">
      {/* TRUE interaction lockout: the full-viewport scrim intercepts every
          pointer event — the page behind is inert. NEVER blur. */}
      <div
        data-scrim="true"
        aria-hidden="true"
        className="absolute inset-0 bg-ink/60"
      />
      {/* Single centered surface (desktop) / bottom sheet (mobile) */}
      <div className="absolute inset-0 flex items-end sm:items-center justify-center pointer-events-none">
        <div
          data-testid="edit-focus-surface"
          role="dialog"
          aria-modal="true"
          aria-label={`${GALLERY_STRINGS.editFocus.title}: ${template.name}`}
          className={cn(
            'pointer-events-auto bg-card border shadow-xl flex flex-col',
            'w-full max-h-[85vh] rounded-t-xl', // mobile: bottom sheet
            'sm:w-auto sm:max-w-3xl sm:rounded-xl sm:max-h-[80vh]', // desktop: centered surface
          )}
        >
          {/* Header */}
          <div className="flex-shrink-0 flex items-center justify-between p-4 border-b">
            <h3 className="font-semibold">
              {GALLERY_STRINGS.editFocus.title}: {template.name}
            </h3>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={onCancel}>
                {GALLERY_STRINGS.editFocus.cancel}
              </Button>
              <Button variant="ghost" size="sm" onClick={onCancel} aria-label={GALLERY_STRINGS.editFocus.cancel}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Body: settings stacked beside the live Shape (one surface,
              control→result mapping at side-by-side distance) */}
          <div className="flex-1 overflow-y-auto p-4 flex flex-col sm:flex-row gap-6">
            <div className="flex-1 flex flex-col gap-3" data-settings-column="true">
              <h4 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                {GALLERY_STRINGS.editFocus.settings}
              </h4>
              {renderField(SURFACE_FIELDS[0])}
              <div className="grid grid-cols-2 gap-3">
                {SURFACE_FIELDS.slice(1).map(renderField)}
              </div>
            </div>

            {/* Live Shape: the SAME clamped model — floor ticks and all.
                Updates ONLY on commit (signal 3). */}
            <div className="sm:w-56 flex-shrink-0" data-shape-column="true">
              <div className="flex items-center gap-2 mb-2">
                <h4 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                  {GALLERY_STRINGS.editFocus.liveShape}
                </h4>
                {/* Signal 2: minimal stale indicator (dot/tag, never banner) */}
                {anyPending && (
                  <span
                    data-preview-stale="true"
                    className="inline-flex items-center gap-1 text-[10px] text-coral-dark"
                  >
                    <span aria-hidden="true">●</span>
                    {GALLERY_STRINGS.editFocus.staleTag}
                  </span>
                )}
              </div>
              <div
                data-commit-tick={shapeKey}
                className="transition-all duration-300"
              >
                <RecipeShape recipe={committed} floor={floor} variant="full" />
              </div>
            </div>
          </div>

          {/* Footer: Cancel + Save always visible */}
          <div className="flex-shrink-0 flex justify-end gap-2 p-4 border-t">
            <Button variant="outline" onClick={onCancel}>
              {GALLERY_STRINGS.editFocus.cancel}
            </Button>
            <Button onClick={handleSave} disabled={isSaving} className="bg-coral hover:bg-coral-hover">
              {GALLERY_STRINGS.editFocus.save}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
